import { Code, Group, Stack, Text } from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowsSplit,
  IconCircleCheck,
  IconLock,
  IconPhoto,
  IconPlayerStop,
  IconQuestionMark,
  IconRepeat,
  IconRobot,
  IconStatusChange,
} from '@tabler/icons-react';
import dagre from '@dagrejs/dagre';
import {
  Background,
  Controls,
  Handle,
  MarkerType,
  MiniMap,
  NodeResizer,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  type Edge,
  type Node,
  type NodeProps,
  type NodeTypes,
} from '@xyflow/react';
import { useEffect } from 'react';
import '@xyflow/react/dist/style.css';
import { useQuestionSet } from '../api/question-sets';
import type {
  CollectImagesStep,
  CollectQuestionsStep,
  DecisionStep,
  OutputStep,
  WorkflowDefinitionGraph,
  WorkflowStep,
} from '../api/workflows';
import { findLoopByControl } from '../pages/workflow-edit-helpers';

// React Flow nodes need a fixed size for dagre to compute a layout —
// these match the default node CSS used elsewhere so layouts line up
// visually across the edit canvas and the read-only viewer.
export const NODE_WIDTH = 180;
export const NODE_HEIGHT = 60;

/**
 * A loop's member steps render inside a visual container (see
 * `buildStepNodes`) rather than the graph's flat node list — this id
 * namespaces the synthetic dagre/React-Flow node representing that
 * container so it can never collide with a real step id.
 */
export function loopGroupId(loopId: string): string {
  return `group:${loopId}`;
}

/**
 * Small lookup indices over a graph's loops, shared by `autoLayout` and
 * `buildEdges` — both need to know "is this step a loop's control?" and
 * "which loop, and what are its members?" to treat loop-internal edges
 * specially.
 */
function indexLoops(graph: WorkflowDefinitionGraph) {
  const entries = Object.entries(graph.loops ?? {});
  return {
    loopIdByControl: new Map(entries.map(([loopId, loop]) => [loop.control, loopId])),
    loopIdByStart: new Map(entries.map(([loopId, loop]) => [loop.start, loopId])),
    loopById: new Map(entries),
  };
}

/**
 * Run dagre on the step graph and return left-to-right positions for
 * every step. Used by the edit canvas to seed brand-new node positions
 * and by the read-only viewer to lay the whole graph out from scratch.
 * Loop members are nested under a synthetic group node per loop (dagre's
 * compound-graph support via `setParent`) so dagre lays each loop out as
 * a contiguous cluster and computes its bounding box automatically — the
 * returned map has one extra `loopGroupId(...)` entry per loop, carrying
 * `width`/`height` alongside `x`/`y` so callers can size/position the
 * container and convert member positions to be relative to it.
 */
export function autoLayout(
  graph: WorkflowDefinitionGraph,
): Map<string, { x: number; y: number; width?: number; height?: number }> {
  const { loopIdByControl, loopById } = indexLoops(graph);
  const g = new dagre.graphlib.Graph({ compound: true });
  // dagre has no per-cluster spacing knob — nodesep/ranksep apply
  // graph-wide, so a loop's members can only be given more breathing
  // room by spacing out the whole canvas a bit (60/80 read as slightly
  // cramped once members sit inside a visible container).
  g.setGraph({ rankdir: 'LR', nodesep: 80, ranksep: 110 });
  g.setDefaultEdgeLabel(() => ({}));
  for (const id of Object.keys(graph.steps)) {
    g.setNode(id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  }
  for (const [sourceId, step] of Object.entries(graph.steps)) {
    for (const target of Object.values(step.transitions ?? {})) {
      if (target === 'TERMINAL' || !graph.steps[target]) continue;
      const sourceLoopId = loopIdByControl.get(sourceId);
      const sourceLoop = sourceLoopId ? loopById.get(sourceLoopId) : undefined;
      // A step exiting a loop from its control (the `done` transition)
      // needs enough rank distance to clear every member of the loop's
      // cluster, not just the control step's own rank within it — a
      // plain `+1` minlen can land the next step visually underneath
      // the container when the control isn't the cluster's last-ranked
      // child.
      if (sourceLoop && !sourceLoop.steps.includes(target)) {
        g.setEdge(sourceId, target, { minlen: sourceLoop.steps.length });
      } else {
        g.setEdge(sourceId, target);
      }
    }
  }
  for (const [loopId, loop] of Object.entries(graph.loops ?? {})) {
    const groupId = loopGroupId(loopId);
    g.setNode(groupId, {});
    for (const memberId of loop.steps) {
      if (graph.steps[memberId]) g.setParent(memberId, groupId);
    }
  }
  dagre.layout(g);
  const out = new Map<string, { x: number; y: number; width?: number; height?: number }>();
  for (const id of Object.keys(graph.steps)) {
    const { x, y } = g.node(id);
    out.set(id, { x: x - NODE_WIDTH / 2, y: y - NODE_HEIGHT / 2 });
  }
  for (const loopId of Object.keys(graph.loops ?? {})) {
    const groupId = loopGroupId(loopId);
    const { x, y, width, height } = g.node(groupId);
    out.set(groupId, { x: x - width / 2, y: y - height / 2, width, height });
  }
  return out;
}

/**
 * Build the React Flow edge list for a workflow graph: one edge per
 * `transitions` entry, labelled with the outcome. Each step's
 * operator-authored `order` is shown as a badge on the node itself
 * (see `nodeLabel`/`WorkflowStepNode`) — there is no separate "order"
 * edge overlay, since every real connection must be drawn explicitly
 * regardless of order and a second overlapping edge type only adds
 * visual noise without adding information.
 *
 * Loops get special treatment since their control step isn't rendered
 * as its own card (see `buildStepNodes`) — the loop's container box
 * stands in for it: a transition from a loop *member* into that loop's
 * control step is an internal "pass complete" edge and is dropped,
 * as is the control's own `repeat` back-edge to its loop's start; any
 * other edge touching the control step (an external entry landing on
 * it directly, or its `done` exit) is kept but rendered from/to the
 * container box instead of the hidden control step. An external entry
 * landing directly on the loop's `start` member (skipping the control
 * step) is likewise rendered onto the box rather than diving inside.
 */
export function buildEdges(graph: WorkflowDefinitionGraph): Edge[] {
  const { loopIdByControl, loopIdByStart, loopById } = indexLoops(graph);

  const edges: Edge[] = [];
  const addEdge = (id: string, source: string, target: string, outcome: string) => {
    edges.push({
      id,
      source,
      target,
      label: outcome,
      // Default (bezier) routing reads as more "flowy" than smoothstep's
      // right-angle routing, especially around loop back-edges — keep it,
      // the arrowhead alone is enough to make direction unambiguous.
      markerEnd: { type: MarkerType.ArrowClosed },
      labelBgStyle: { fill: '#fff', fillOpacity: 0.9 },
    });
  };

  for (const [sourceId, step] of Object.entries(graph.steps)) {
    for (const [outcome, target] of Object.entries(step.transitions ?? {})) {
      if (target === 'TERMINAL' || !graph.steps[target]) continue;
      const id = `${sourceId}-${outcome}-${target}`;

      const targetLoopId = loopIdByControl.get(target);
      if (targetLoopId) {
        const loop = loopById.get(targetLoopId)!;
        // A member's own completion edge into its control step is
        // internal — dropped. An edge from outside the loop landing
        // directly on the control step is a genuine entry, kept and
        // rendered onto the box.
        if (loop.steps.includes(sourceId)) continue;
        addEdge(id, sourceId, loopGroupId(targetLoopId), outcome);
        continue;
      }

      const sourceLoopId = loopIdByControl.get(sourceId);
      if (sourceLoopId) {
        const loop = loopById.get(sourceLoopId)!;
        // `repeat` targets the loop's own start member — an internal
        // back-edge, dropped. Anything else (`done`) exits the loop,
        // rendered from the container.
        if (loop.steps.includes(target)) continue;
        addEdge(id, loopGroupId(sourceLoopId), target, outcome);
        continue;
      }

      const targetStartLoopId = loopIdByStart.get(target);
      const targetStartLoop = targetStartLoopId ? loopById.get(targetStartLoopId) : undefined;
      const isExternalStartEntry =
        targetStartLoop !== undefined && !targetStartLoop.steps.includes(sourceId);
      addEdge(
        id,
        sourceId,
        isExternalStartEntry ? loopGroupId(targetStartLoopId!) : target,
        outcome,
      );
    }
  }
  return edges;
}

export function isTerminalKind(kind: string): boolean {
  return kind === 'output' || kind === 'emit_final' || kind === 'halt';
}

/**
 * "Is this step missing required config?" — lightweight client-side
 * mirror of the orchestrator validator's authoring expectations. Used
 * to surface a ⚠ marker on under-configured nodes. Not exhaustive;
 * server-side validate() runs at save time and catches the rest.
 */
export function stepNeedsAttention(step: WorkflowStep): boolean {
  const needsOnComplete =
    step.kind === 'collect_questions' ||
    step.kind === 'collect_images' ||
    step.kind === 'run_ai_review';
  if (needsOnComplete && !step.transitions?.on_complete) return true;
  if (step.kind === 'collect_questions') {
    const id = (step.params as { question_set_id?: string } | undefined)?.question_set_id;
    return !id;
  }
  if (step.kind === 'collect_images') {
    const cfgs = (step.params as { configurations?: unknown[] } | undefined)?.configurations;
    return !cfgs || cfgs.length === 0;
  }
  if (step.kind === 'decision') {
    const p = step.params as
      | { input?: { step_id?: string; question_id?: string }; branches?: unknown[] }
      | undefined;
    return !p?.input?.step_id || !p?.input?.question_id || !p?.branches || p.branches.length === 0;
  }
  if (step.kind === 'output') {
    const fields = (step.params as { fields?: unknown[] } | undefined)?.fields;
    return !fields || fields.length === 0;
  }
  if (step.kind === 'gate_on_consent') {
    // Branching gate: needs a selected consent plus both an on_pass
    // (granted) and on_fail (missing consent) target.
    const hasConsent = !!(step.params as { types?: string[] } | undefined)?.types?.length;
    return !hasConsent || !step.transitions?.on_pass || !step.transitions?.on_fail;
  }
  if (step.kind === 'halt') {
    const rc = (step.params as { reason_code?: string } | undefined)?.reason_code;
    return !rc;
  }
  if (step.kind === 'set_case_status') {
    const status = (step.params as { status?: string } | undefined)?.status;
    return !status || !step.transitions?.on_complete;
  }
  if (step.kind === 'loop_control') {
    // Membership/start completeness needs the owning loop, which this
    // step-only signature can't see (same limitation every other kind
    // has here) — the server validator is authoritative for that. This
    // client-side check only catches the one thing visible on the step
    // itself: the required `done` transition.
    return !step.transitions?.done;
  }
  return false;
}

const KIND_LABELS: Record<string, string> = {
  gate_on_consent: 'Consent gate',
  require_images: 'Require images',
  run_ai_review: 'AI analysis',
  request_human_review: 'Human review',
  condition: 'Condition',
  emit_webhook: 'Webhook',
  emit_final: 'Final output',
  set_case_status: 'Set case status',
  halt: 'Halt',
  collect_questions: 'Collect questions',
  collect_images: 'Collect images',
  decision: 'Decision',
  output: 'Output',
  loop_control: 'Loop control',
};

export function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind;
}

const KIND_ICONS: Record<string, React.ComponentType<{ size?: number | string }>> = {
  gate_on_consent: IconLock,
  collect_questions: IconQuestionMark,
  collect_images: IconPhoto,
  decision: IconArrowsSplit,
  run_ai_review: IconRobot,
  set_case_status: IconStatusChange,
  loop_control: IconRepeat,
  output: IconCircleCheck,
  halt: IconPlayerStop,
};

export function kindIcon(kind: string): React.ComponentType<{ size?: number | string }> {
  return KIND_ICONS[kind] ?? IconAlertTriangle;
}

/**
 * Membership-count + max_iterations summary for a `loop_control` step,
 * e.g. "4 steps, max 3". `stepSummary`/`nodeLabel` only see the step
 * itself, not the owning graph, so callers that have the full
 * `WorkflowDefinitionGraph` in scope (buildStepNodes, the edit canvas)
 * compute this and pass it through as `loopSummary`.
 */
export function loopSummaryFor(
  graph: WorkflowDefinitionGraph,
  controlStepId: string,
): string | null {
  const found = findLoopByControl(graph, controlStepId);
  if (!found) return null;
  const [, loop] = found;
  const count = loop.steps.length;
  return `${count} step${count === 1 ? '' : 's'}, max ${loop.max_iterations ?? 3}`;
}

export function stepSummary(
  step: WorkflowStep,
  consentBlockingByCode?: Map<string, boolean>,
  loopSummary?: string | null,
): string | null {
  const p = (step.params ?? {}) as Record<string, unknown>;
  switch (step.kind) {
    case 'loop_control':
      return loopSummary ?? null;
    case 'gate_on_consent': {
      const code = (p.types as string[] | undefined)?.[0];
      if (!code) return null;
      const blocking = consentBlockingByCode?.get(code);
      return blocking === undefined ? code : `${code} · ${blocking ? 'blocking' : 'optional'}`;
    }
    case 'collect_images': {
      const cfgs = (p.configurations as string[] | undefined) ?? [];
      return cfgs.length ? cfgs.join(', ') : null;
    }
    case 'require_images': {
      const n = p.min_count as number | undefined;
      return typeof n === 'number' ? `min ${n}` : null;
    }
    case 'output': {
      const n = (p.fields as unknown[] | undefined)?.length ?? 0;
      return n ? `${n} field${n === 1 ? '' : 's'}` : null;
    }
    case 'halt':
      return (p.reason_code as string | undefined) ?? null;
    case 'set_case_status':
      return (p.status as string | undefined) ?? null;
    case 'emit_webhook':
      return (p.event_name as string | undefined) ?? null;
    case 'request_human_review':
      return (p.tier as string | undefined) ?? null;
    case 'decision': {
      const q = (p.input as { question_id?: string } | undefined)?.question_id;
      return q ? `on ${q}` : null;
    }
    default:
      return null;
  }
}

/**
 * Compact textual badges keep the default React Flow node renderer (no
 * custom node type needed). Order is operator-authored metadata; ▶
 * marks the start step picked up by the runtime; ⚠ flags steps missing
 * required configuration.
 */
export function nodeLabel(
  // Retained for call-site compatibility; the id is shown in the side
  // panel, not the node label, so it's intentionally unrendered here.
  _id: string,
  step: WorkflowStep,
  isStart: boolean,
  needsAttention: boolean,
  consentBlockingByCode?: Map<string, boolean>,
  loopSummary?: string | null,
): string {
  const badges = `${needsAttention ? '⚠ ' : ''}${isStart ? '▶ ' : ''}${step.order ? `${step.order}. ` : ''}`;
  const summary = stepSummary(step, consentBlockingByCode, loopSummary);
  return `${badges}${kindLabel(step.kind)}${summary ? `\n${summary}` : ''}`;
}

// ─── Read-only viewer with config-rich nodes ─────────────────────────────────

interface WorkflowStepNodeData extends Record<string, unknown> {
  stepId: string;
  step: WorkflowStep;
  isStart: boolean;
  isTerminal: boolean;
  isLoopMember: boolean;
  isSelected: boolean;
  needsAttention: boolean;
  consentBlockingByCode?: Map<string, boolean>;
  loopSummary?: string | null;
}

/**
 * Shared card-style React Flow node for both the read-only viewer and
 * the edit canvas. Renders the kind icon + heading and a per-kind
 * config summary so operators can read the workflow's shape at a
 * glance without clicking each box. `isSelected` draws a selection
 * ring on the edit canvas; the read-only viewer never sets it.
 */
function WorkflowStepNode({ data }: NodeProps) {
  const d = data as WorkflowStepNodeData;
  const { step, isStart, isTerminal, isLoopMember, isSelected, needsAttention } = d;
  // Selection ring matches each accent's own hue instead of a one-size
  // -fits-all blue — a blue ring around an already-saturated red/green
  // border reads as clashing rather than "selected." Unselected border
  // intensity is otherwise untouched: a terminal node marks the end of
  // a flow and needs to stay unmissable at a glance.
  const accent = isStart
    ? {
        border: '2px solid var(--mantine-color-green-6)',
        bg: 'var(--mantine-color-green-0)',
        ring: 'var(--mantine-color-green-4)',
      }
    : isTerminal
      ? {
          // Kept at full intensity (red-6, 2px) — a terminal node is
          // the end of a flow and needs to read as unmissable; only
          // the selection ring is colour-matched (red, not blue).
          border: '2px solid var(--mantine-color-red-6)',
          bg: 'var(--mantine-color-red-0)',
          ring: 'var(--mantine-color-red-4)',
        }
      : isLoopMember
        ? {
            border: '1px dashed var(--mantine-color-violet-6)',
            bg: '#fff',
            ring: 'var(--mantine-color-blue-5)',
          }
        : { border: '1px solid #c4c4c4', bg: '#fff', ring: 'var(--mantine-color-blue-5)' };
  const Icon = kindIcon(step.kind);

  return (
    <div
      style={{
        border: accent.border,
        backgroundColor: accent.bg,
        borderRadius: 6,
        padding: '8px 12px',
        minWidth: 200,
        maxWidth: 260,
        fontFamily: 'inherit',
        boxShadow: isSelected ? `0 0 0 2px ${accent.ring}` : undefined,
      }}
    >
      <Handle type="target" position={Position.Left} />
      <Stack gap={2}>
        <Group gap={6} wrap="nowrap">
          <Icon size={14} />
          <Text size="sm" fw={600}>
            {needsAttention ? '⚠ ' : ''}
            {isStart ? '▶ ' : ''}
            {step.order ? `${step.order}. ` : ''}
            {kindLabel(step.kind)}
          </Text>
        </Group>
        <StepConfigSummary
          step={step}
          consentBlockingByCode={d.consentBlockingByCode}
          loopSummary={d.loopSummary}
        />
      </Stack>
      <Handle type="source" position={Position.Right} />
    </div>
  );
}

function StepConfigSummary({
  step,
  consentBlockingByCode,
  loopSummary,
}: {
  step: WorkflowStep;
  consentBlockingByCode?: Map<string, boolean>;
  loopSummary?: string | null;
}) {
  if (step.kind === 'gate_on_consent') {
    const s = stepSummary(step, consentBlockingByCode);
    return s ? (
      <Text size="xs" c="dimmed">
        {s}
      </Text>
    ) : null;
  }
  if (step.kind === 'loop_control') {
    const s = stepSummary(step, undefined, loopSummary);
    return s ? (
      <Text size="xs" c="dimmed">
        {s}
      </Text>
    ) : null;
  }
  if (step.kind === 'collect_questions') {
    return <CollectQuestionsSummary step={step as CollectQuestionsStep} />;
  }
  if (step.kind === 'collect_images') {
    return <CollectImagesSummary step={step as CollectImagesStep} />;
  }
  if (step.kind === 'decision') {
    return <DecisionSummary step={step as DecisionStep} />;
  }
  if (step.kind === 'output') {
    return <OutputSummary step={step as OutputStep} />;
  }
  return null;
}

function CollectQuestionsSummary({ step }: { step: CollectQuestionsStep }) {
  const id = step.params.question_set_id;
  const { data } = useQuestionSet(id);
  if (!id) {
    return (
      <Text size="xs" c="red">
        No question set wired
      </Text>
    );
  }
  return (
    <Text size="xs">
      Set: <strong>{data?.name ?? id.slice(0, 8)}</strong>
      {data ? ` · ${data.questions.length}q` : ''}
    </Text>
  );
}

const IMAGE_PATTERN_LABEL: Record<string, string> = {
  derm_only: 'derm only',
  derm_plus_macro: 'derm + macro',
  macro_only: 'macro only',
};

function CollectImagesSummary({ step }: { step: CollectImagesStep }) {
  const cfgs = step.params.configurations ?? [];
  if (cfgs.length === 0) {
    return (
      <Text size="xs" c="red">
        No image patterns
      </Text>
    );
  }
  const derm = step.params.dermoscopic_count;
  const macro = step.params.macroscopic_count;
  return (
    <Stack gap={0}>
      <Text size="xs">{cfgs.map((c) => IMAGE_PATTERN_LABEL[c] ?? c).join(' / ')}</Text>
      {derm && (
        <Text size="xs" c="dimmed">
          derm {derm.min}–{derm.max}
          {derm.optional ? ' (opt)' : ''}
        </Text>
      )}
      {macro && (
        <Text size="xs" c="dimmed">
          macro {macro.min}–{macro.max}
          {macro.optional ? ' (opt)' : ''}
        </Text>
      )}
    </Stack>
  );
}

function DecisionSummary({ step }: { step: DecisionStep }) {
  const branches = step.params.branches ?? [];
  const qid = step.params.input?.question_id;
  return (
    <Text size="xs">
      {branches.length} branch{branches.length === 1 ? '' : 'es'}
      {qid ? (
        <>
          {' '}
          on <Code>{qid}</Code>
        </>
      ) : (
        ''
      )}
    </Text>
  );
}

function OutputSummary({ step }: { step: OutputStep }) {
  const fields = step.params.fields ?? [];
  if (fields.length === 0) {
    return (
      <Text size="xs" c="red">
        No output fields
      </Text>
    );
  }
  return <Text size="xs">Returns: {fields.map((f) => f.name).join(', ')}</Text>;
}

interface LoopGroupNodeData extends Record<string, unknown> {
  label: string;
  controlStepId: string;
  // Driven by the page's own `selectedId` (matching `controlStepId`),
  // not React Flow's native node-selection state — same reasoning as
  // `WorkflowStepNode`'s `isSelected`: our custom click handler can
  // deselect (toggle back to null) without React Flow's own internal
  // selection tracking knowing, so relying on the native `selected`
  // prop here would leave the ring on after a close.
  isSelected: boolean;
}

/**
 * Visual container for a loop — stands in for the loop's `loop_control`
 * step itself (see `buildStepNodes`, which skips rendering a separate
 * card for the control step and points this node's `data.controlStepId`
 * at it instead), with the loop's other member steps parented inside as
 * children. Selectable/draggable/deletable/resizable: selecting or
 * deleting this box acts on the control step (the edit canvas resolves
 * `data.controlStepId`), and dragging or resizing it moves/resizes the
 * whole loop as a unit. Not connectable — `done`/`start`/membership are
 * wired through the side panel, not by dragging a new connection here.
 */
function LoopGroupNode({ data }: NodeProps) {
  const { label, isSelected } = data as LoopGroupNodeData;
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        // Selected switches dashed → solid (same violet family as the
        // unselected state, not an unrelated colour) plus a soft violet
        // halo — a clearer "this is selected" cue than the dashed
        // border alone gave, and consistent with how the step cards
        // now ring in their own accent colour rather than a generic blue.
        border: isSelected
          ? '2px solid var(--mantine-color-violet-7)'
          : '2px dashed var(--mantine-color-violet-6)',
        backgroundColor: 'var(--mantine-color-violet-0)',
        borderRadius: 8,
        position: 'relative',
        boxShadow: isSelected ? '0 0 0 2px var(--mantine-color-violet-3)' : undefined,
      }}
    >
      <NodeResizer
        isVisible={isSelected}
        minWidth={NODE_WIDTH}
        minHeight={NODE_HEIGHT}
        lineStyle={{ borderColor: 'var(--mantine-color-violet-6)' }}
        handleStyle={{ backgroundColor: 'var(--mantine-color-violet-6)', width: 8, height: 8 }}
      />
      <Handle type="target" position={Position.Left} />
      <Text
        size="xs"
        fw={600}
        c="violet"
        style={{
          position: 'absolute',
          top: -11,
          left: 8,
          padding: '0 6px',
          backgroundColor: 'var(--mantine-color-body)',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Text>
      <Handle type="source" position={Position.Right} />
    </div>
  );
}

export const WORKFLOW_NODE_TYPES: NodeTypes = {
  workflowStep: WorkflowStepNode,
  loopGroup: LoopGroupNode,
};

/**
 * Build the React Flow node list for a workflow graph: the shared
 * `workflowStep` card node (icon, badges, per-kind config summary) for
 * every step except a loop's control step, plus one `loopGroup`
 * container node per loop that stands in for that control step (see
 * `LoopGroupNode`) with the loop's other members parented to it
 * (`parentId` + `extent: 'parent'`). Used by both the read-only viewer
 * and the edit canvas so a step looks identical whether you're viewing
 * or editing it. `selectedId` drives the node's selection ring — pass
 * the page's currently-selected step id (or omit it for the read-only
 * viewer, which has no selection). Group nodes must precede their
 * member nodes in the returned array — React Flow requires a parent to
 * be defined before any node that references it via `parentId`.
 */
export function buildStepNodes(
  graph: WorkflowDefinitionGraph,
  opts: { consentBlockingByCode?: Map<string, boolean>; selectedId?: string | null } = {},
): Node[] {
  const positions = autoLayout(graph);
  const controlStepIds = new Set(Object.values(graph.loops ?? {}).map((loop) => loop.control));
  const groupIdByMember = new Map<string, string>();
  for (const [loopId, loop] of Object.entries(graph.loops ?? {})) {
    const groupId = loopGroupId(loopId);
    for (const memberId of loop.steps) {
      if (memberId !== loop.control && graph.steps[memberId])
        groupIdByMember.set(memberId, groupId);
    }
  }

  const groupNodes: Node[] = Object.entries(graph.loops ?? {}).map(([loopId, loop]) => {
    const groupId = loopGroupId(loopId);
    const pos = positions.get(groupId);
    const summary = loopSummaryFor(graph, loop.control);
    const controlStep = graph.steps[loop.control];
    const needsAttention = controlStep ? stepNeedsAttention(controlStep) : false;
    const label = `${needsAttention ? '⚠ ' : ''}🔁 Loop${summary ? ` · ${summary}` : ''}`;
    return {
      id: groupId,
      type: 'loopGroup',
      data: {
        label,
        controlStepId: loop.control,
        isSelected: loop.control === opts.selectedId,
      } satisfies LoopGroupNodeData,
      position: { x: pos?.x ?? 0, y: pos?.y ?? 0 },
      width: pos?.width ?? NODE_WIDTH,
      height: pos?.height ?? NODE_HEIGHT,
      selectable: true,
      draggable: true,
      connectable: false,
      deletable: true,
    };
  });

  const stepNodes: Node[] = Object.entries(graph.steps)
    .filter(([id]) => !controlStepIds.has(id))
    .map<Node>(([id, step]) => {
      const isStart = graph.start_step === id;
      const isTerminal = isTerminalKind(step.kind);
      const needsAttention = stepNeedsAttention(step);
      const groupId = groupIdByMember.get(id);
      const abs = positions.get(id) ?? { x: 0, y: 0 };
      const groupPos = groupId ? positions.get(groupId) : undefined;
      const position = groupPos ? { x: abs.x - groupPos.x, y: abs.y - groupPos.y } : abs;
      return {
        id,
        type: 'workflowStep',
        ...(groupId ? { parentId: groupId, extent: 'parent' as const } : {}),
        data: {
          stepId: id,
          step,
          isStart,
          isTerminal,
          isLoopMember: groupId !== undefined,
          isSelected: id === opts.selectedId,
          needsAttention,
          consentBlockingByCode: opts.consentBlockingByCode,
        } satisfies WorkflowStepNodeData,
        position,
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
      };
    });

  return [...groupNodes, ...stepNodes];
}

/**
 * Whether a node's existing on-canvas position should carry over into
 * the next render instead of resetting to the freshly computed layout
 * position. Used by the edit canvas to preserve operator drags across
 * definition updates. A node whose parent changed (joined/left a loop
 * container) can't keep its old position — for a parented node that
 * position is relative to its (new) parent's origin, so the stale
 * coordinate would place it somewhere nonsensical.
 */
export function shouldPreservePosition(
  prevNode: { parentId?: string } | undefined,
  freshNode: { parentId?: string },
): boolean {
  return prevNode !== undefined && prevNode.parentId === freshNode.parentId;
}

/**
 * The step id a click, drag, or delete on this node should act on. A
 * `loopGroup` node stands in for its loop's control step (see
 * `buildStepNodes`) — selecting, dragging, or deleting the box should
 * resolve to that step's real id, not the synthetic group id, so the
 * side panel and definition mutations target the actual step.
 */
export function resolveStepId(node: { id: string; data: Record<string, unknown> }): string {
  const controlStepId = node.data.controlStepId;
  return typeof controlStepId === 'string' ? controlStepId : node.id;
}

/**
 * Read-only canvas viewer. Renders a workflow's step graph with the
 * same colors, badges, and edges the edit page shows — but with
 * dragging, selecting, and connecting disabled. The container caller
 * controls the height; React Flow fills it.
 */
export function WorkflowFlowView({
  definition,
  height = 480,
  consentBlockingByCode,
}: {
  definition: WorkflowDefinitionGraph;
  height?: number | string;
  consentBlockingByCode?: Map<string, boolean>;
}) {
  // Manage nodes + edges in local state so node drags persist within
  // the page lifetime. Without this, passing `nodes` as a controlled
  // prop without `onNodesChange` would have React Flow re-apply the
  // dagre positions on every render — the visual position would snap
  // back the instant the user released the drag.
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>(
    buildStepNodes(definition, { consentBlockingByCode }),
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(buildEdges(definition));

  // Reseed when the underlying definition changes (e.g., the parent
  // re-fetches the workflow). useNodesState only seeds initial state,
  // so we have to push fresh nodes/edges in ourselves.
  useEffect(() => {
    setNodes(buildStepNodes(definition, { consentBlockingByCode }));
    setEdges(buildEdges(definition));
  }, [definition, consentBlockingByCode, setNodes, setEdges]);

  return (
    <div style={{ width: '100%', height, border: '1px solid #ddd', borderRadius: 4 }}>
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={WORKFLOW_NODE_TYPES}
          // Nodes are draggable so operators can rearrange complex
          // graphs for legibility. Positions aren't persisted across
          // page reloads — they live in component state and reset to
          // the dagre auto-layout on remount. Connecting + selecting
          // stay disabled: the detail view never mutates the
          // definition.
          nodesDraggable
          nodesConnectable={false}
          elementsSelectable={false}
          edgesFocusable={false}
          panOnDrag
          zoomOnScroll
          fitView
        >
          <Background />
          <Controls showInteractive={false} />
          <MiniMap pannable zoomable style={{ height: 100 }} />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
}
